"use client";
import findAuthorizedVendorCount from "@/app/ec/product/[basePath]/findAuthorizedVendorCount";
import findLowestVendorDeliveryDay from "@/app/ec/product/[basePath]/findLowestVendorDeliveryDay";
import findLowestVendorPrice from "@/app/ec/product/[basePath]/findLowestVendorPrice";
import doesVendorHaveLowestDeliveryDate from "@/app/ec/product/[basePath]/vendor-options/doesVendorHaveLowestDeliveryDate";
import getStockedMessage from "@/app/ec/product/[basePath]/vendor-options/getStockedMessage";
import useAnalyticsManager, { UserShippingAnalyticsInfo } from "@/app/hooks/useAnalyticsManager";
import { useGetPostalCodeDetailsQuery } from "@/store/userApi";
import {
  AdditionalAnalyticsProductInfo,
  AnalyticsReportProduct,
  AnalyticsReportProductV2,
} from "@/utils/analyticsEventSender";
import { FunctionComponent, useEffect, useState } from "react";

type Props = {
  mpId: number;
  availabilityState: string;
  categoriesTree: CategoryTree[];
  canonicalURL: string;
  productDetails: ProductDetails;
  vendorOptions: VendorOptionState[];
  vendorIndex?: number;
  quantity: number;
  zipCode?: string | null;
  brandName: string;
};
const ProductGTMLoad: FunctionComponent<Props> = ({
  mpId,
  availabilityState,
  categoriesTree,
  canonicalURL,
  productDetails,
  vendorIndex: chosenVendorKey = 0,
  vendorOptions,
  zipCode,
  // brandName,
}) => {
  const [gtmDataAlreadyCollected, setGTMDataAlreadyCollected] = useState(false);
  const { dispatchPageView, dispatchProductDetailPageView } = useAnalyticsManager();
  const [pdpViewItemReportSent, setPDPViewItemReportSent] = useState<boolean>(false);
  const [userShippingAnalyticsInfo, setUserShippingAnalyticsInfo] = useState<UserShippingAnalyticsInfo>();
  const { data: postalCodeDetils, isLoading: postalCodeDetailsLoding } = useGetPostalCodeDetailsQuery(
    zipCode ? zipCode : "27513",
  );

  if (userShippingAnalyticsInfo == null && postalCodeDetailsLoding == false && postalCodeDetils != undefined) {
    setUserShippingAnalyticsInfo({
      shippingState: postalCodeDetils.state,
      shippingSubRegion: postalCodeDetils.subRegion,
    });
  }
  useEffect(() => {
    if (!mpId || !productDetails || !categoriesTree || categoriesTree.length == 0 || gtmDataAlreadyCollected) {
      return;
    }
    setGTMDataAlreadyCollected(true);
    const analyticsPDPUserCart: AnalyticsReportProduct = {
      id: mpId,
      name: productDetails.title,
      brand: productDetails.brandName,
      // used for facebook pdp view event
      category: categoriesTree[categoriesTree.length - 1].name,
      // used for klaviyo pdp view event
      categories: [categoriesTree[categoriesTree.length - 1].name],
      price:
        availabilityState === "for-sale" && vendorOptions
          ? parseFloat(vendorOptions[0].productSubTotal.toString())
          : undefined,
      image_url: `${process.env.NEXT_PUBLIC_BASE_URL}/media${productDetails?.mediaPath}`,
      product_detail_page_url: canonicalURL,
      retail_price: availabilityState === "for-sale" ? parseFloat(productDetails.retailPrice.toString()) : undefined,
    };

    //pageViewWithAdditionalInfo event
    dispatchPageView({
      product: {
        mpId: mpId,
        category: categoriesTree.length > 0 ? categoriesTree[categoriesTree.length - 1].name : undefined,
      },
    });
  }, [
    mpId,
    categoriesTree.length,
    productDetails,
    gtmDataAlreadyCollected,
    categoriesTree,
    availabilityState,
    vendorOptions,
    canonicalURL,
    dispatchPageView,
  ]);

  useEffect(() => {
    if (
      pdpViewItemReportSent === false &&
      productDetails.title &&
      vendorOptions &&
      vendorOptions[chosenVendorKey] &&
      categoriesTree.length > 0 &&
      userShippingAnalyticsInfo
    ) {
      setPDPViewItemReportSent(true);
      const fullfillmentMessage = getStockedMessage(vendorOptions[chosenVendorKey]);
      const product: AnalyticsReportProductV2 = {
        item_id: mpId.toString(),
        item_name: productDetails.title,
        price: vendorOptions[chosenVendorKey].chosenPriceBreakUnitPrice,
        quantity: vendorOptions[chosenVendorKey]?.minimumQuantityPurchasable,
        item_brand: productDetails.brandName,
        item_category: categoriesTree[0] ? categoriesTree[0].name : undefined,
        item_category2: categoriesTree[1] ? categoriesTree[1].name : undefined,
        item_category3: categoriesTree[2] ? categoriesTree[2].name : undefined,
        item_category4: categoriesTree[3] ? categoriesTree[3].name : undefined,
        item_category5: categoriesTree[4] ? categoriesTree[4].name : undefined,
        item_absolute_url: `https://www.net32.com${productDetails.url}`,
        item_absolute_image: productDetails.mediaPath
          ? `https://www.net32.com/media${productDetails.mediaPath}`
          : undefined,
        item_retail_price: availabilityState ? parseFloat(productDetails.retailPrice.toString()) : undefined,
        item_specific_category: categoriesTree[categoriesTree.length - 1].name,
      };
      console.log(`PRODUCT DETAILS ${JSON.stringify(product)}`);
      const additionalProductInfo: AdditionalAnalyticsProductInfo = {
        buybox_winning_vendor: vendorOptions[chosenVendorKey].vendorName,
        buybox_winning_vendor_isAuthorized: vendorOptions[chosenVendorKey].isAuthorizedDistributor ? "yes" : "no",
        buybox_delivery_days: vendorOptions[chosenVendorKey].arrivalBusinessDays,
        buybox_handling_time: vendorOptions[chosenVendorKey].shippingTime,
        buybox_isLowestPrice: vendorOptions[chosenVendorKey].isLowestPrice ? "yes" : "no",
        buybox_isLowestDeliveryDays: doesVendorHaveLowestDeliveryDate(
          vendorOptions[chosenVendorKey].vendorId,
          vendorOptions,
        )
          ? "yes"
          : "no",
        buybox_fulfillment_message: fullfillmentMessage !== "null" ? fullfillmentMessage : null,
        all_vendors_count: vendorOptions.length,
        all_vendors_isAuthorized_count: findAuthorizedVendorCount(vendorOptions),
        all_vendors_lowest_delivery_days: findLowestVendorDeliveryDay(vendorOptions),
        all_vendors_lowest_price: findLowestVendorPrice(vendorOptions),
        user_shipping_state: userShippingAnalyticsInfo.shippingState,
        user_shipping_subRegion: userShippingAnalyticsInfo.shippingSubRegion,
      };
      //view_item event
      dispatchProductDetailPageView(
        product,
        additionalProductInfo,
        (product.price ? product.price : 0) * product.quantity,
      );
    }
  }, [
    categoriesTree,
    chosenVendorKey,
    mpId,
    productDetails.brandName,
    productDetails.title,
    vendorOptions,
    userShippingAnalyticsInfo,
    pdpViewItemReportSent,
    dispatchProductDetailPageView,
  ]);

  return null;
};

export default ProductGTMLoad;
